import React, { useState, useEffect } from 'react';
import { 
  Trophy, 
  Medal, 
  Award, 
  TrendingUp, 
  Heart, 
  Bookmark, 
  ChefHat, 
  Star, 
  Coins, 
  Brain, 
  Crown, 
  Zap,
  Calendar,
  Filter,
  Users,
  BarChart3
} from 'lucide-react';
import { CommunityLeaderboard, LeaderboardEntry } from '../../types/users';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';

interface CommunityLeaderboardProps {
  className?: string;
}

const CommunityLeaderboardComponent: React.FC<CommunityLeaderboardProps> = ({ className = '' }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'all_time'>('weekly');
  const [selectedCategory, setSelectedCategory] = useState<string>('most_liked');
  const [leaderboards, setLeaderboards] = useState<CommunityLeaderboard[]>([]);

  const { getLeaderboards, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('community-leaderboard');

  useEffect(() => {
    loadLeaderboards();
  }, [selectedPeriod, isExampleData]);

  const loadLeaderboards = () => {
    if (isExampleData) {
      // Load from centralized example data
      const exampleLeaderboards = getLeaderboards();
      setLeaderboards(exampleLeaderboards as CommunityLeaderboard[]);
    } else {
      // Load from real API
      // const apiLeaderboards = await fetchLeaderboardsFromAPI(selectedPeriod);
      // setLeaderboards(apiLeaderboards);
      setLeaderboards([]);
    }
  };

  const categories = [
    {
      id: 'most_liked',
      name: 'Most Liked Contributors',
      icon: Heart,
      description: 'Contributors receiving the most likes',
      color: 'text-red-500'
    },
    {
      id: 'most_saved',
      name: 'Most Saved Recipes',
      icon: Bookmark,
      description: 'Recipes saved most often by the community',
      color: 'text-blue-500'
    },
    {
      id: 'most_cooked',
      name: 'Most Cooked Recipes',
      icon: ChefHat,
      description: 'Recipes that users actually make',
      color: 'text-green-500'
    },
    {
      id: 'most_reviewed',
      name: 'Most Reviewed',
      icon: Star,
      description: 'Recipes with the most community reviews',
      color: 'text-yellow-500'
    },
    {
      id: 'most_helpful_reviews',
      name: 'Most Helpful Reviewers',
      icon: Award,
      description: 'Users writing the most helpful reviews',
      color: 'text-purple-500'
    },
    {
      id: 'most_ai_contributions',
      name: 'AI Analysis Champions',
      icon: Brain,
      description: 'Contributors investing most in community data',
      color: 'text-purple-600'
    }
  ];

  const periods = [
    { id: 'daily', name: 'Today', icon: Calendar },
    { id: 'weekly', name: 'This Week', icon: Calendar },
    { id: 'monthly', name: 'This Month', icon: Calendar },
    { id: 'all_time', name: 'All Time', icon: Trophy }
  ];

  const currentLeaderboard = leaderboards.find(lb => lb.category === selectedCategory);
  const selectedCategoryInfo = categories.find(cat => cat.id === selectedCategory);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Award className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-gray-500 font-bold">{rank}</span>;
    }
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) {
      return <TrendingUp className="w-3 h-3 text-green-500" />;
    } else if (change < 0) {
      return <TrendingUp className="w-3 h-3 text-red-500 transform rotate-180" />;
    }
    return null;
  };

  const formatValue = (value: number, category: string) => {
    if (category === 'most_ai_contributions') {
      return `${value} coins`;
    }
    return value.toLocaleString();
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
      {/* Example Data Disclaimer */}
      {isExampleData && (
        <DataDisclaimer 
          type="info"
          title="Example Community Leaderboard"
          message="This shows example community rankings and statistics. Real community data will be available when users start contributing."
          className="m-6 mb-0"
        />
      )}

      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Trophy className="w-6 h-6 mr-2 text-yellow-500" />
              Community Leaderboard
              {isExampleData && <MockBadge variant="small" className="ml-2" />}
            </h2>
            <p className="text-gray-600 mt-1">Celebrating our amazing contributors and popular recipes</p>
          </div>
          
          <div className="flex items-center space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {periods.map(period => (
                <option key={period.id} value={period.id}>
                  {period.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2">
          {categories.map(category => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className={`w-4 h-4 mr-1 inline ${category.color}`} />
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Leaderboard Content */}
      <div className="p-6">
        {selectedCategoryInfo && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-1 flex items-center">
              <selectedCategoryInfo.icon className={`w-5 h-5 mr-2 ${selectedCategoryInfo.color}`} />
              {selectedCategoryInfo.name}
            </h3>
            <p className="text-sm text-gray-600">{selectedCategoryInfo.description}</p>
          </div>
        )}

        {currentLeaderboard && (
          <div className="space-y-3">
            {currentLeaderboard.rankings.map((entry, index) => (
              <div
                key={`${entry.rank}-${entry.displayName}`}
                className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                  entry.highlight
                    ? 'border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-4">
                  {/* Rank */}
                  <div className="flex items-center justify-center w-10 h-10">
                    {getRankIcon(entry.rank)}
                  </div>
                  
                  {/* Profile/Recipe Info */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">{entry.displayName}</h4>
                      {entry.badge && (
                        <span className="text-lg">{entry.badge}</span>
                      )}
                      {entry.rank <= 3 && (
                        <Crown className="w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                    
                    {/* Value and Change */}
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-lg font-bold text-gray-900">
                        {formatValue(entry.value, selectedCategory)}
                      </span>
                      
                      {entry.change !== 0 && (
                        <div className="flex items-center space-x-1">
                          {getChangeIcon(entry.change)}
                          <span className={`text-xs ${
                            entry.change > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {entry.change > 0 ? '+' : ''}{entry.change}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Action Button */}
                <div className="flex items-center space-x-2">
                  {selectedCategory.includes('recipe') ? (
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View Recipe
                    </button>
                  ) : (
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View Profile
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Community Stats */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Community Impact This Week
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">2,847</div>
              <div className="text-xs text-gray-500">Total Likes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">1,234</div>
              <div className="text-xs text-gray-500">Recipes Cooked</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">15,670</div>
              <div className="text-xs text-gray-500">ZippyCoins Invested</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">89</div>
              <div className="text-xs text-gray-500">AI Analyses</div>
            </div>
          </div>
        </div>

        {/* Special Recognition */}
        <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <Zap className="w-6 h-6 text-purple-600" />
            <div>
              <h4 className="font-medium text-purple-900">Special Recognition</h4>
              <p className="text-sm text-purple-800 mt-1">
                <strong>Chef Maria Rodriguez</strong> contributed the most AI analysis data this week, 
                helping enhance <strong>47 recipes</strong> for the entire community! 🎉
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityLeaderboardComponent; 
